/**
 * The echarts instance lifecycle for the charts the app still draws with its own echarts:
 * the metric view screen's full-size chart and the site analytics report's donut and bars.
 * The Superset chart host and the drilldown's table take MAX_BOX_WAIT_FRAMES and
 * memoisedImport from here too.
 *
 * Imports no echarts — the caller hands the module in. Its consumers are reachable from
 * app.js, the single Vite entry on every layout, so a charting import here would ship the
 * whole payload on the login screen and undo the charts' lazy loading.
 *
 * Every branch below is a bug someone already paid for: the zero-width container wait, the
 * instance reuse instead of dispose-per-render, the devicePixelRatio rebuild, the render
 * token, and the best-effort teardown. They lived as two near-identical copies, which meant
 * the next fix had to be hand-mirrored with nothing to signal a miss — this is the one copy.
 */

/**
 * ~1s of frames. Then draw regardless, so a permanently hidden container cannot wedge the chart.
 * Exported for superset-chart-lifecycle.js, which waits the same budget but parks the draw instead.
 */
export const MAX_BOX_WAIT_FRAMES = 60;

/**
 * Memoise a dynamic import on SUCCESS ONLY.
 *
 * A cached rejection would be handed to every later caller for the rest of the session.
 * This does not buy a retry over the network — once a module URL fails to fetch the browser
 * records it in its module map and every later import() of that specifier resolves to the
 * same rejection without a request — but it does give a clean retry when the failure was not
 * the fetch (a registration that threw, say), and it keeps a rejection from being cached
 * forever. The user-facing side belongs to the caller's load-failed flag.
 */
export function memoisedImport(load) {
    let promise = null;

    return () => {
        if (!promise) {
            promise = load();
            promise.catch(() => { promise = null; });
        }

        return promise;
    };
}

/**
 * @param {object}   config
 * @param {string}   config.label          Names this chart in console messages.
 * @param {Function} config.getContainer   The element the chart should currently be bound to.
 * @param {Function} config.isCurrent      (token) => whether that render is still the newest.
 * @param {Function} config.invalidate     Bumps the caller's render token, dropping any
 *                                         in-flight draw. Called by clear() and cleanup().
 * @param {Function} config.rerender       Called when the device pixel ratio moves.
 * @param {Function} config.onError        Called when a draw throws, after teardown.
 */
export function createEchartsLifecycle({ label, getContainer, isCurrent, invalidate, rerender, onError }) {
    let instance = null;
    let observer = null;
    let dpr = null;

    function cleanup() {
        // Invalidate first. A draw can be parked in its rAF wait for a container that is
        // hidden or being removed, and it only bails on a token mismatch — so without this a
        // teardown mid-wait lets that loop run to completion and init a fresh instance, plus
        // a ResizeObserver, on a node nothing will ever dispose.
        invalidate();

        // Best effort — the references are dropped either way — but it still reports: a throw
        // here means the observer or the instance is in a state we did not expect.
        if (observer) {
            try {
                observer.disconnect();
            } catch (error) {
                console.debug(`ResizeObserver disconnect failed during ${label} cleanup:`, error);
            }
            observer = null;
        }

        if (instance) {
            try {
                instance.dispose();
            } catch (error) {
                console.debug(`echarts dispose failed during ${label} cleanup:`, error);
            }
            instance = null;
        }
    }

    function drawWhenBoxed(echarts, container, option, token, attempt = 0) {
        if (!isCurrent(token) || getContainer() !== container) {
            return;
        }

        // echarts bakes its layout into the container's measured size at init(), so a container
        // that is still display:none locks in a collapsed grid — and resizing afterwards
        // stretches the canvas without rescuing the layout, leaving the axis-trigger tooltip's
        // hit area outside the visible chart.
        if (container.clientWidth === 0 && attempt < MAX_BOX_WAIT_FRAMES) {
            requestAnimationFrame(() => drawWhenBoxed(echarts, container, option, token, attempt + 1));
            return;
        }

        try {
            const nextDpr = window.devicePixelRatio || 1;
            let chart = echarts.getInstanceByDom(container);

            // zrender reads devicePixelRatio once at module-import time and reuses it on every
            // resize, so after a browser zoom or a move to a different-DPI display the backing
            // store is the wrong scale and every stroke is resampled soft. Rebuild when it moves.
            if (chart && !chart.isDisposed() && dpr !== nextDpr) {
                chart.dispose();
                chart = null;
            }

            if (!chart || chart.isDisposed()) {
                chart = echarts.init(container, null, { devicePixelRatio: nextDpr });
                dpr = nextDpr;

                // echarts has no responsive mode; watch the container for size changes.
                if (typeof ResizeObserver !== 'undefined') {
                    if (observer) {
                        observer.disconnect();
                    }

                    observer = new ResizeObserver(() => {
                        if (!instance || instance.isDisposed()) return;

                        // A zoom change fires a resize; re-render so the canvas is rebuilt at the
                        // new ratio.
                        if ((window.devicePixelRatio || 1) !== dpr) {
                            rerender();
                            return;
                        }

                        instance.resize();
                    });
                    observer.observe(container);
                }
            }

            instance = chart;
            // notMerge, and never dispose-then-init per render: disposing leaves echarts' queued
            // pipeline tasks pointing at a torn-down model, whose series then have no
            // coordinateSystem — the dataSample processor reads coordSys.type and throws
            // asynchronously, which silently kills the tooltip.
            chart.setOption(option, { notMerge: true });
        } catch (error) {
            console.error(`Error rendering the ${label}:`, error);
            cleanup();
            onError(error);
        }
    }

    return {
        drawWhenBoxed,
        cleanup,
        /**
         * Wipe the drawing without tearing the instance down (the no-data path).
         *
         * Disposing here would race echarts' async pipeline — see drawWhenBoxed's notMerge
         * comment. Invalidates for the same reason cleanup() does: the payload flipping to
         * no-data has to drop a draw that is still awaiting its chunks, or that draw lands
         * on a container the blade has since hidden, burns the whole rAF wait and then
         * initialises against a zero-width box — the exact collapsed layout the wait exists
         * to avoid.
         */
        clear() {
            invalidate();

            if (instance && !instance.isDisposed()) {
                instance.clear();
            }
        },
    };
}
